import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type {
  OnboardCompanyRequest,
  OnboardedCompany,
  ProvisioningMode,
  ScimTokenListItem,
} from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";

// The multi-tenant app registration that the provisioning worker uses to read users through
// Microsoft Graph. Each tenant of a company must give it admin consent.
export const GRAPH_APP_CLIENT_ID =
  import.meta.env.VITE_ENVIRONMENT === "production"
    ? "118b543a-83ea-46d2-ab69-3c9331069a4f"
    : "81a4da1e-30ea-46ae-b052-2e81cb9f1631";

// A tenant administrator opens this link to grant admin consent to the app registration.
export function adminConsentUrl(
  tenantId: string,
  clientId: string = GRAPH_APP_CLIENT_ID,
): string {
  return `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/adminconsent?client_id=${encodeURIComponent(clientId)}`;
}

// keycloak-api refuses a sync for a company that is not active or imported, or has no tenant.
export function canSyncNow(company: OnboardedCompany): boolean {
  return (
    (company.status === "active" || company.status === "imported") &&
    // The worker skips a scim tenant, so a company of scim tenants has nothing to pull.
    company.tenants.some((t) => t.provisioningMode !== "scim")
  );
}

export function useOnboardedCompanies() {
  return useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/companies`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<OnboardedCompany[]>;
    },
  });
}

// Onboards a new company, completes an imported row, adds tenants or domains, or retries a
// failed run. All of these are the same call, and it is safe to repeat.
export function useOnboardCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      body,
    }: {
      companyId: string;
      body: OnboardCompanyRequest;
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/companies/${encodeURIComponent(companyId)}/onboard`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<OnboardedCompany>;
    },
    // A failed step is recorded on the row, so refresh the list also after an error.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["companies"] }),
  });
}

// The provider admin routes of one tenant (ADR-0062). The tenant belongs to the company.
function tenantUrl(companyId: string, tenantId: string, path: string): string {
  return `${API_BASE}/companies/${encodeURIComponent(companyId)}/tenants/${encodeURIComponent(tenantId)}/${path}`;
}

interface TenantRef {
  companyId: string;
  tenantId: string;
}

// scim without a live token answers 409 kc.scim_mode_requires_token. graph schedules a full
// pull and revokes the tenant's tokens.
export function useSetProvisioningMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      tenantId,
      mode,
    }: TenantRef & { mode: ProvisioningMode }) => {
      const res = await authorizedFetch(
        tenantUrl(companyId, tenantId, "provisioning-mode"),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode }),
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["scim-tokens"] });
    },
  });
}

export function useScimTokens(
  { companyId, tenantId }: TenantRef,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["scim-tokens", tenantId],
    enabled,
    queryFn: async () => {
      const res = await authorizedFetch(
        tenantUrl(companyId, tenantId, "scim-tokens"),
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ScimTokenListItem[]>;
    },
  });
}

// The answer holds the token once. A later read cannot show it again.
export function useCreateScimToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, tenantId }: TenantRef) => {
      const res = await authorizedFetch(
        tenantUrl(companyId, tenantId, "scim-tokens"),
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ id: string; token: string }>;
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["scim-tokens"] }),
  });
}

export function useRevokeScimToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      tenantId,
      tokenId,
    }: TenantRef & { tokenId: string }) => {
      const res = await authorizedFetch(
        tenantUrl(
          companyId,
          tenantId,
          `scim-tokens/${encodeURIComponent(tokenId)}`,
        ),
        { method: "DELETE" },
      );
      await throwIfNotOk(res, "kc");
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["scim-tokens"] }),
  });
}

// A manual preview. Never fetched on its own, so a refetch is a deliberate click.
export function useScimScope({ companyId, tenantId }: TenantRef) {
  return useQuery({
    queryKey: ["scim-scope", tenantId],
    enabled: false,
    gcTime: 0,
    queryFn: async () => {
      const res = await authorizedFetch(
        tenantUrl(companyId, tenantId, "scim-scope"),
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ outOfScope: number }>;
    },
  });
}

export function useApplyScimScope() {
  return useMutation({
    mutationFn: async ({ companyId, tenantId }: TenantRef) => {
      const res = await authorizedFetch(
        tenantUrl(companyId, tenantId, "scim-scope/apply"),
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ disabled: number }>;
    },
  });
}
